/**
 * Stage 2 choice writers — pure transforms applied only on an explicit user
 * action from the decision checklist.
 *
 * Each intent writer returns the FULL next intent (the store writes verbatim)
 * and stamps `fieldSource = 'accepted'`, so the existing USER-ALWAYS-WINS
 * guards protect the value afterwards. A field the user already answered —
 * including a recorded unknown — is never overwritten. The proposal's origin
 * (evidence basis, report ids) is recorded on `site.stage2DecisionReview`,
 * outside the intent, by the review helpers below.
 */
import type {
  PhasePowerComponentKey,
  PhasePowerSourceConfig,
  ProjectIntent,
  ProjectPhase,
} from '../../types/siteRag';
import {
  clearIntentFieldExplicitlyUnknown,
  isIntentFieldExplicitlyUnknown,
  markIntentFieldExplicitlyUnknown,
} from '../intent/explicitUnknown';
import { withPhasePowerSourceConfig } from '../projectIntent';
import { OPTION_COMPONENT } from './stage2Evidence';
import type {
  Stage2DecisionId,
  Stage2DecisionReview,
  Stage2Proposal,
} from './stage2Types';

type FieldSourceKey = keyof NonNullable<ProjectIntent['fieldSource']>;

function stampAccepted(intent: ProjectIntent, field: FieldSourceKey): ProjectIntent {
  const cleared = clearIntentFieldExplicitlyUnknown(intent, field);
  return {
    ...cleared,
    fieldSource: { ...(cleared.fieldSource ?? {}), [field]: 'accepted' } as ProjectIntent['fieldSource'],
  };
}

function phaseHasTechnology(phase: ProjectPhase): boolean {
  return (phase.powerSourceConfig?.components.length ?? 0) > 0
    || (phase.powerSource != null && phase.powerSource !== 'undecided');
}

/** Apply an accepted cooling proposal to the parts that are still open. */
export function applyCoolingProposal(
  intent: ProjectIntent,
  proposal: Extract<Stage2Proposal, { kind: 'cooling' }>,
): ProjectIntent {
  let next = intent;
  const sinkOpen = next.bopHeatRejection == null || next.bopHeatRejection === 'unknown';
  if (proposal.bopHeatRejection && sinkOpen) {
    next = stampAccepted({ ...next, bopHeatRejection: proposal.bopHeatRejection }, 'bopHeatRejection');
  }
  const loopOpen = next.coolingStrategy == null || next.coolingStrategy === 'undecided';
  if (proposal.coolingStrategy && loopOpen) {
    next = stampAccepted({ ...next, coolingStrategy: proposal.coolingStrategy }, 'coolingStrategy');
  }
  return next;
}

/**
 * Apply the screening pathway's per-phase supply options to phases that still
 * have no technology. A lone combined-cycle choice is written as the coarse
 * `gas-cc` key: the component vocabulary has a single `gas-turbine` entry that
 * the Generation & Impact workbench seeds as simple cycle, so writing it would
 * silently change the technology. Mixed choices use components, and the cycle
 * type is chosen in the workbench.
 */
export function applyPhasePowerProposal(
  intent: ProjectIntent,
  proposal: Extract<Stage2Proposal, { kind: 'phase-power' }>,
): ProjectIntent {
  const byId = new Map(proposal.phases.map(entry => [entry.phaseId, entry]));
  let changed = false;
  const phases = (intent.phases ?? []).map(phase => {
    const entry = byId.get(phase.id);
    if (!entry || phaseHasTechnology(phase) || entry.options.length === 0) return phase;
    changed = true;
    if (entry.options.length === 1 && entry.options[0] === 'gas-cc' && !entry.battery) {
      return { ...phase, powerSource: 'gas-cc' as const, powerSourceConfig: undefined };
    }
    const components = [...new Set<PhasePowerComponentKey>([
      ...entry.options.map(option => OPTION_COMPONENT[option]),
      ...(entry.battery ? ['battery' as const] : []),
    ])];
    const config: PhasePowerSourceConfig = { components, gridConnection: entry.gridConnection };
    return withPhasePowerSourceConfig(phase, config);
  });
  if (!changed) return intent;
  return stampAccepted({ ...intent, phases }, 'phases');
}

/** Record the still-open fields of a decision as explicitly unknown. */
export function recordStage2DecisionUnknown(
  intent: ProjectIntent,
  id: Stage2DecisionId,
): ProjectIntent {
  const markIfOpen = (current: ProjectIntent, field: string, open: boolean): ProjectIntent =>
    open && !isIntentFieldExplicitlyUnknown(current, field) ? markIntentFieldExplicitlyUnknown(current, field) : current;
  switch (id) {
    case 'cooling': {
      let next = markIfOpen(intent, 'coolingStrategy', intent.coolingStrategy == null || intent.coolingStrategy === 'undecided');
      if (next.bopHeatRejection == null) {
        next = markIntentFieldExplicitlyUnknown({ ...next, bopHeatRejection: 'unknown' }, 'bopHeatRejection');
      }
      return next;
    }
    case 'build-phases':
      return markIfOpen(intent, 'timeline', true);
    case 'design-basis': {
      const next = markIfOpen(intent, 'pueValue', typeof intent.pueValue !== 'number');
      return markIfOpen(next, 'datacenterTierTarget', next.datacenterTierTarget == null && next.redundancyClass == null);
    }
    case 'firm-capacity':
      return markIfOpen(intent, 'capacityCreditIntent', intent.capacityCreditIntent?.target == null);
    case 'water-supply':
      return markIfOpen(intent, 'waterSource', true);
    case 'grid-path':
      return markIfOpen(intent, 'interconnectionStatus', intent.interconnectionStatus?.queueStatus == null);
    case 'fuel-supply':
      return markIfOpen(intent, 'fuelAccess', intent.fuelAccess?.supply == null);
    case 'nuclear':
      return markIfOpen(intent, 'reactorDesignClass', intent.reactorDesignClass == null);
    case 'commercial': {
      const next = markIfOpen(intent, 'offtake', intent.offtake?.structure == null);
      return markIfOpen(next, 'carbonCommitment', next.carbonCommitment == null);
    }
    case 'power-supply':
      // The strategy is a report-setup choice with no unknown sentinel: the
      // scenario needs an answer, so the checklist offers "Decide later".
      return intent;
    default: {
      const exhaustive: never = id;
      void exhaustive;
      return intent;
    }
  }
}

/** Decisions whose unknown can be recorded (the power strategy cannot). */
export function canRecordStage2Unknown(id: Stage2DecisionId): boolean {
  return id !== 'power-supply';
}

export function withStage2Deferral(
  review: Stage2DecisionReview | null | undefined,
  id: Stage2DecisionId,
  basisKey: string,
  at: string,
): Stage2DecisionReview {
  return { ...(review ?? {}), deferred: { ...(review?.deferred ?? {}), [id]: { at, basisKey } } };
}

export function withoutStage2Deferral(
  review: Stage2DecisionReview | null | undefined,
  id: Stage2DecisionId,
): Stage2DecisionReview {
  const deferred = { ...(review?.deferred ?? {}) };
  delete deferred[id];
  return { ...(review ?? {}), deferred };
}

export function withStage2Acceptance(
  review: Stage2DecisionReview | null | undefined,
  id: Stage2DecisionId,
  record: { basisKey: string; reportIds: string[]; summary: string; at: string },
): Stage2DecisionReview {
  const deferred = { ...(review?.deferred ?? {}) };
  delete deferred[id];
  return {
    ...(review ?? {}),
    deferred,
    accepted: { ...(review?.accepted ?? {}), [id]: record },
  };
}
